import {
  call,
  put,
  delay,
  takeLatest,
  takeLeading,
  all,
  race,
  select,
  take,
} from 'redux-saga/effects';
import axios from 'axios';
import updateActions, { actionTypes } from '../reducers/UpdateChecker';
import updateApi from '../../api/SWUpdateApi';
import {
  selectors as protectionSelectors,
  actionTypes as protectionActionTypes,
} from '../reducers/Protection';

import {
  selectors as replicationSelectors,
  actionTypes as replcationActionTypes,
} from 'js/redux/reducers/Replication';
import { fulfilled } from '../factories/ApiCall';
import { randomBetween } from '../../mylib/Utils';
import { CURRENT_VERSION } from '../../../version';
import log from '../../api/Logger';
import { offline_mode } from '../../Constants';
import { waitForConfigValues } from './Configuration';

const automaticUpdateCheckingEnabled = (configValues) =>
  Number(configValues.automatic_updates_logic ?? 2) >= 2;

function* waitForProtectionStatus() {
  let status = yield select(protectionSelectors.status);
  while (!status) {
    const action = yield take(protectionActionTypes.FETCH_STATUS_FULLFILLED);
    status = action.payload;
  }
  return status;
}

function* checkRMOnce(manual = false) {
  const { data } = yield call(() => axios({ url: '/version' }));
  yield put({ type: actionTypes.VERSION, payload: data });

  const versions = {
    url: window.location.origin + '/',
    front_version_js: CURRENT_VERSION,
    ...data,
  };
  yield call(() =>
    manual
      ? updateApi.check_files_manual(versions)
      : updateApi.check_files(versions)
  );
}

function* prepareHQCheck() {
  const { data } = yield call(() => axios({ url: '/version' }));
  yield put({ type: actionTypes.VERSION, payload: data });
  yield put({ type: replcationActionTypes.REPLICATION_FETCH_DETAILS });

  const { has_replication } = yield call(waitForReplication);
  yield call(() =>
    updateApi.set_db_versions({
      ...data,
      has_replication,
    })
  );
}

// return selected site, waiting for SET_SITE if necessary
export function* waitForReplication() {
  let details = (yield select(replicationSelectors.details))?.data;
  while (!details) {
    const a = yield take(
      fulfilled(replcationActionTypes.REPLICATION_FETCH_DETAILS)
    );
    details = a.payload;
  }
  return details;
}
/**
 * Saga worker.
 * Note the Axios call might be good to move under API folder...
 */
function* pollSagaWorker() {
  try {
    let configValues = yield call(waitForConfigValues);
    if (!automaticUpdateCheckingEnabled(configValues)) return;

    // Wait 5 seconds before starting the update check
    yield delay(5000);
    const status = yield call(waitForProtectionStatus);
    while (status.cloud) {
      configValues = yield call(waitForConfigValues);
      if (!automaticUpdateCheckingEnabled(configValues)) return;
      try {
        yield call(checkRMOnce);
      } catch (err) {
        log.error(err);
      }
      // once per 200 seconds check the version for production change this to something reasonable!!
      yield delay(200000);
    }
  } catch (e) {
    log.error(e);
  }
}

/**
 * Internet update from my.innovatint.com
 * @param action
 */
function* myInnovatintCom() {
  try {
    let configValues = yield call(waitForConfigValues);
    if (!automaticUpdateCheckingEnabled(configValues)) return;

    yield delay(15000);
    const status = yield call(waitForProtectionStatus);
    // Check version from my.innovatint.com once at startup and after that randomly in 18 to 24 hour intervals
    while (!status.cloud && !offline_mode) {
      configValues = yield call(waitForConfigValues);
      if (!automaticUpdateCheckingEnabled(configValues)) return;
      try {
        yield call(prepareHQCheck);
        yield call(() => updateApi.check_my_innovatint_com());
      } catch (e) {
        log.error(e);
      }
      // Random delay for rechecking between 18 to 24 hours from startup
      const wait_time = Math.round(randomBetween(18, 24) * 60);
      log.info(`Update recheck from HQ in ${wait_time} minutes`);
      yield delay(wait_time * 60 * 1000);
    }
  } catch (err) {
    log.error(err);
  }
}

function* checkNow() {
  try {
    const configValues = yield call(waitForConfigValues);
    if (Number(configValues.automatic_updates_logic ?? 2) === 0 || offline_mode)
      return;

    yield put(
      updateActions.setSWupdateState({
        state: 'STATE_CHECKING_UPDATES',
        tasks: [],
        notes: '',
      })
    );

    const status = yield call(waitForProtectionStatus);

    if (status.cloud) {
      yield call(checkRMOnce, true);
    } else {
      yield call(prepareHQCheck);
      yield call(() => updateApi.check_my_innovatint_com_manual());
    }
  } catch (err) {
    log.error(err);
    yield put(
      updateActions.setSWupdateState({
        state: 'STATE_ERROR_ON_UPDATE',
        tasks: [],
        notes: err instanceof Error ? err.message : String(err),
      })
    );
  }
}

function* approveDownload() {
  try {
    yield call(() => updateApi.approve_download());
  } catch (err) {
    log.error(err);
  }
}

function* startUpdate() {
  try {
    yield call(() => updateApi.start_update());
  } catch (err) {
    log.error(err);
  }
}

function* runUntilStopped(worker) {
  yield race({
    worker: call(worker),
    stopped: take(actionTypes.POLL_STOP),
  });
}

/**
 * Saga watcher.
 */
export default function* saga() {
  yield all([
    yield takeLatest(actionTypes.POLL_START, runUntilStopped, pollSagaWorker),
    yield takeLatest(actionTypes.APPROVE_DOWNLOAD, approveDownload),
    yield takeLatest(actionTypes.START_UPDATE, startUpdate),
    yield takeLatest(actionTypes.POLL_START, runUntilStopped, myInnovatintCom),
    yield takeLeading(actionTypes.CHECK_NOW, runUntilStopped, checkNow),
  ]);
}
